import { useGetDetailInstitution } from '@vindr/services/actions/institution.action';
import { useGetListReport } from '@vindr/services/actions/report.action';
import { ResponseGetDetailInstitution } from '@vindr/services/apis/institution.api';
import { ResponseGetLayouts } from '@vindr/services/apis/layout.api';
import { ResponseGetOrders } from '@vindr/services/apis/order.api';
import { ReportByVersionId, Reports } from '@vindr/services/apis/report.api';
import { ResponseFetchStudyList } from '@vindr/services/apis/study.api';
import { GENDER } from '@vindr/services/constants/common.constant';
import { REPORT_VARIABLES } from '@vindr/services/constants/report.constant';
import { StructuredReportElement } from '@vindr/ui/src/components/common/StructuredReport/components/elements/types';
import {
  MARGIN_BOTTOM_PAGE,
  MARGIN_LEFT_PAGE,
  MARGIN_RIGHT_PAGE,
  MARGIN_TOP_PAGE,
  WIDTH_PRINT_PAGE,
  pagePrintStyle,
} from '@vindr/ui/src/components/ui/TextEditor';
import { autoSpace, convertPhoneNumber } from '@vindr/utils/formatText/formatText';
import { getDateFormatted, groupByKey } from '@vindr/utils/helpers/common.helper';
import { generateQRCode } from '@vindr/utils/helpers/qrcode';
import useToggle from '@vindr/utils/hooks/useToggle';
import getActual from '@vindr/utils/print/getActual';
import printFile from '@vindr/utils/print/printFile';
import {
  generateShareLink,
  generateVisitLink,
  getAccessKeys,
} from '@vindr/utils/report/generateLink';
import Converter from '@vindr/utils/unit-converter';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseTranslationResponse, useTranslation } from 'react-i18next';

const convertUnits = new Converter('px', 'mm', 'inch').convertUnits;
export type MergedReport = Omit<Reports, keyof ReportByVersionId> & ReportByVersionId;
interface useGenerateReportParams {
  layout?: ResponseGetLayouts['data'][0];
  order?: ResponseGetOrders['data'][0];
  report?: MergedReport;
  reports?: ReturnType<typeof useGetListReport>[number]['data'][];
  structureReportSections?: StructuredReportElement[];
  study?: ResponseFetchStudyList['data'][0];
}

const useGenerateReport = ({
  layout,
  order,
  report,
  reports,
  structureReportSections,
  study,
}: useGenerateReportParams) => {
  const { t } = useTranslation('Vindoc');
  const [preview, setPreview] = useState('');
  const { value: isLoading, setFalse: setLoaded, setTrue: setLoading } = useToggle();

  const { data: institutionResponse } = useGetDetailInstitution(order?.institution_id, {
    enabled: !reports?.length && !!order?.institution_id,
  });

  const institution = institutionResponse?.data;
  let approver = report?.approver;
  approver = reports?.length ? null : approver;

  const createPreview = useCallback(async () => {
    if (!reports?.length) {
      const preview = await createSinglePreview({
        report,
        order,
        layout,
        study,
        institution,
        approver,
        structureReportSections,
        t,
      });
      return setPreview(preview);
    }

    const previews = await Promise.all(
      reports.map(async (it) => {
        return createSinglePreview({
          report: it.recentReport,
          order: it.recentOrder,
          layout: null,
          study: it.study,
          institution: it.institutionDetail,
          approver: it.approver,
          structureReportSections,
          t,
        });
      })
    );

    setPreview(previews.join('<div class="break-page"></div>'));
  }, [approver, institution, layout, order, report, reports, study, t, structureReportSections]);

  useEffect(() => {
    createPreview();
  }, [createPreview]);

  const srcDoc = useMemo(
    () =>
      `<html style="width: calc(${WIDTH_PRINT_PAGE}mm - ${
        MARGIN_LEFT_PAGE + MARGIN_RIGHT_PAGE
      }mm); margin: 0;">
        <head>
          <style>${pagePrintStyle}</style>
        </head>
        <body>
        ${preview}
        <script>
          ${setFooterSpace}
        </script>
        <script>
          ${removePage}
        </script>
        </body>
      </html>`,
    [preview]
  );

  const customHandlePrint = (src: HTMLIFrameElement | string, afterPrint?: () => void) => (
    setLoading(), handlePrint(src, afterPrint, setLoaded)
  );

  return { srcDoc, handlePrint: customHandlePrint, isLoading };
};

export default useGenerateReport;

const createSinglePreview = async (reportData: ReportData): Promise<string> => {
  const { report, order, layout, study, institution, approver, structureReportSections, t } =
    reportData;

  const studyData = {
    study_instance_uid: study?.study_instance_uid,
    modality: study?.modality || study?.modalities,
    visit_id: study?.visit_id,
  };

  const { qrBase64, qrVisitID } = await generateQRCodesForReport({
    accessKeys: report?.share_access_keys,
    studyData,
  });

  return getReportPreview({
    report,
    order,
    layout,
    study,
    institution,
    approver,
    qrBase64,
    t,
    qrVisitID,
    structureReportSections,
  });
};

interface ReportData extends useGenerateReportParams {
  t: (val: string) => string;
  institution: ResponseGetDetailInstitution['data'];
  approver?: null | MergedReport['approver'];
}

const generateQRCodesForReport = async ({
  accessKeys,
  studyData,
}: {
  accessKeys: MergedReport['share_access_keys'];
  studyData: { study_instance_uid?: string; modality?: string; visit_id?: string };
}): Promise<{ qrBase64: string; qrVisitID: string }> => {
  const { study_access_key, visit_access_key } = getAccessKeys(accessKeys) || {};

  const shareLink = generateShareLink(
    study_access_key?.study_instance_uid || studyData.study_instance_uid,
    study_access_key?.key,
    studyData.modality
  );

  const visitLink = generateVisitLink({
    visit_id: visit_access_key?.visit_id || studyData.visit_id,
    access_key: visit_access_key?.key,
  });

  const [qrBase64, qrVisitID] = await Promise.all([
    shareLink ? generateQRCode(shareLink) : '',
    visitLink ? generateQRCode(visitLink) : '',
  ]);

  return { qrBase64, qrVisitID };
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function getReportPreview({
  report,
  order,
  layout,
  study,
  institution,
  approver,
  qrBase64 = null,
  t,
  qrVisitID = null,
  structureReportSections,
}: {
  report: MergedReport;
  order: ResponseGetOrders['data'][0];
  layout: ResponseGetLayouts['data'][0];
  study: ResponseFetchStudyList['data'][0];
  institution: ReturnType<typeof useGetDetailInstitution>['data']['data'];
  approver: MergedReport['approver'];
  qrBase64: string | null;
  t: UseTranslationResponse<string, any>['t'];
  qrVisitID: string | null;
  structureReportSections: StructuredReportElement[];
}) {
  const { study_date: StudyDateTime } = study || {};

  const {
    content,
    conclusion: ReportConclusion,
    approved_at: ReportDateTime,
    submitter,
    layout: ReportLayout,
    share_access_keys,
    is_structure_report,
    technician,
  } = report;
  const ReportContent = content;
  const structureReportData =
    is_structure_report && structureReportSections
      ? groupByKey(structureReportSections, 'name')
      : {};

  const { visit_access_key } = getAccessKeys(share_access_keys) || {};

  const reportApprover = approver || report?.approver;

  const Approver =
    reportApprover && autoSpace(reportApprover?.title || '', reportApprover?.fullname || '');
  const Submitter = submitter && `${submitter?.title || ''}${submitter?.fullname || ''}`;
  const Signature = reportApprover?.signature;

  const {
    logo: InstitutionLogo,
    name: InstitutionName,
    address: InstitutionAddress,
    phone: InstitutionPhone,
    email: InstitutionEmail,
  } = institution || {};

  const {
    order_doctor_name: OrderDoctorName,
    refer_doctor_name: ReferDoctorName,
    diagnostic: Diagnosis,
    visited_patient: patient,
    order_date: OrderDate,
    procedure,
  } = order || {};

  const ProcedureName = procedure?.name;

  const {
    patient_id: PatientID,
    visit_id: VisitID,
    name: PatientName,
    birth_date: PatientBirthday,
    age: PatientAge,
    sex: PatientSex,
    address: PatientAddress,
    phone: PatientPhone,
  } = patient || {};

  const layoutContent = ReportLayout?.content || layout?.content;

  // const { signature: Signature } = userInfo || {}; // using signature of approver

  if (!layoutContent) return '';

  const PrintDateTime = dayjs().format(getDateFormatted(true));

  const layoutConverter = [
    [
      REPORT_VARIABLES.StudyDateTime.code,
      StudyDateTime ? dayjs(StudyDateTime).format(getDateFormatted(true)) : '',
    ],
    [REPORT_VARIABLES.ReportContent.code, ReportContent || ''],
    [REPORT_VARIABLES.ReportConclusion.code, ReportConclusion || ''],
    [
      REPORT_VARIABLES.ReportDateTime.code,
      ReportDateTime ? dayjs(ReportDateTime).format(getDateFormatted(true)) : '',
    ],
    [REPORT_VARIABLES.Approver.code, Approver || ''],
    [REPORT_VARIABLES.Submitter.code, Submitter || ''],
    [
      REPORT_VARIABLES.InstitutionLogo.code,
      imageTag({ src: InstitutionLogo, styles: 'max-height: 100px; margin: 0 auto;' }),
    ],
    [REPORT_VARIABLES.InstitutionName.code, InstitutionName || ''],
    [REPORT_VARIABLES.InstitutionAddress.code, InstitutionAddress || ''],
    [REPORT_VARIABLES.InstitutionPhone.code, InstitutionPhone || ''],
    [REPORT_VARIABLES.InstitutionEmail.code, InstitutionEmail || ''],
    [REPORT_VARIABLES.ProcedureName.code, ProcedureName || ''],
    [REPORT_VARIABLES.OrderDoctorName.code, OrderDoctorName || ''],
    [REPORT_VARIABLES.ReferDoctorName.code, ReferDoctorName || ''],
    [REPORT_VARIABLES.Diagnosis.code, Diagnosis || ''],
    [REPORT_VARIABLES.PatientID.code, PatientID || ''],
    [REPORT_VARIABLES.VisitID.code, VisitID || ''],
    [REPORT_VARIABLES.PatientName.code, PatientName || ''],
    [
      REPORT_VARIABLES.PatientBirthday.code,
      PatientBirthday ? dayjs(PatientBirthday).format(getDateFormatted()) : '',
    ],
    [REPORT_VARIABLES.PatientAge.code, PatientAge ?? ''],
    [REPORT_VARIABLES.PatientSex.code, t(GENDER[PatientSex]) || ''],
    [REPORT_VARIABLES.PatientAddress.code, PatientAddress || ''],
    [REPORT_VARIABLES.PatientPhone.code, convertPhoneNumber(PatientPhone)],
    [REPORT_VARIABLES.PrintDateTime.code, PrintDateTime || ''],
    [
      REPORT_VARIABLES.Signature.code,
      imageTag({
        src: Signature,
        styles: logoStyle,
        replaceValue: REPORT_VARIABLES.Signature.code,
      }),
    ],
    [REPORT_VARIABLES.QRCode.code, imageTag({ src: qrBase64 })],
    [REPORT_VARIABLES.Password.code, generatePasswordFromPhone(PatientPhone)],
    [REPORT_VARIABLES.Today.code, dayjs().format(getDateFormatted())],
    [
      REPORT_VARIABLES.PatientBirthdayYear.code,
      PatientBirthday ? dayjs(PatientBirthday).format('YYYY') : '',
    ],
    [REPORT_VARIABLES.QRVisit.code, imageTag({ src: qrVisitID })],
    [
      REPORT_VARIABLES.QRVisitPassword.code,
      (visit_access_key?.password || '').replaceAll('$', '$$$$'), // double $ will become one $ on replace, $$$$ mean should replace with $$
    ],
    [
      REPORT_VARIABLES.OrderDate.code,
      OrderDate ? dayjs(OrderDate).format(getDateFormatted(true)) : '',
    ],
    [
      REPORT_VARIABLES.ClinicalInformation.code,
      structureReportData?.CLINICAL_INFORMATION?.render?.() || '',
    ],
    [REPORT_VARIABLES.Comparison.code, structureReportData?.COMPARISON?.render?.() || ''],
    [REPORT_VARIABLES.Technique.code, structureReportData?.TECHNIQUE?.render?.() || ''],
    [REPORT_VARIABLES.Findings.code, structureReportData?.FINDINGS?.render?.() || ''],
    [REPORT_VARIABLES.Impression.code, structureReportData?.IMPRESSION?.render?.() || ''],
    [
      REPORT_VARIABLES.EsignSubmitter.code,
      `<span style='font-size: 0.1pt'>${REPORT_VARIABLES.EsignSubmitter.code}</span>`,
    ],
    [
      REPORT_VARIABLES.EsignApprover.code,
      `<span style='font-size: 0.1pt'>${REPORT_VARIABLES.EsignApprover.code}</span>`,
    ],
    [
      REPORT_VARIABLES.SignatureSubmitter.code,
      imageTag({ src: submitter?.signature, styles: logoStyle }),
    ],
    [
      REPORT_VARIABLES.SignatureApprover.code,
      imageTag({ src: approver?.signature, styles: logoStyle }),
    ],
    [
      REPORT_VARIABLES.SignatureTechnician.code,
      imageTag({ src: technician?.signature, styles: logoStyle }),
    ],
    [REPORT_VARIABLES.Technician.code, technician?.fullname ?? technician?.username ?? ''],
  ];

  return layoutConverter.reduce((acc, [key, value]) => {
    return acc.replaceAll(key, value);
  }, layoutContent);
}

function imageTag({
  src,
  styles = '',
  replaceValue = '',
}: {
  src: string;
  styles?: string;
  replaceValue?: string;
}) {
  return src ? `<img style="padding: 2px; ${styles}" src=${src} />` : replaceValue;
}

function generatePasswordFromPhone(phone: string) {
  return phone ? `${phone.substring(phone.length - 4)}a@` : '';
}

function handlePrint(
  iframe: HTMLIFrameElement | string,
  afterPrint?: () => void,
  onLoaded?: () => void
) {
  if (!iframe) return;

  if (typeof iframe === 'string') return printFile(iframe, afterPrint, onLoaded);

  const contentDocument = iframe.contentDocument;
  const contentWindow = iframe.contentWindow;
  const body = contentDocument.body;
  const html = contentDocument.documentElement;
  const heightPrintContent = convertUnits(
    Math.max(
      body.scrollHeight,
      body.offsetHeight
      // html.clientHeight,
      // html.scrollHeight,
      // html.offsetHeight
    ) ||
      getActual(iframe, 'scrollHeight') ||
      0,
    'px',
    'mm'
  );

  const [A4_WIDTH_MM, A4_HEIGHT_MM] = [210, 297];
  const header = contentDocument.querySelector('thead');
  const footer = contentDocument.querySelector('tfoot');

  // if (!footer) return contentWindow.print();
  // const printElement = footer?.closest?.('table');
  // if (!printElement) return contentWindow.print();

  contentWindow.addEventListener('beforeprint', (e) => {
    const footerHeight = convertUnits(footer?.clientHeight ?? 0, 'px', 'mm');
    const headerHeight = convertUnits(header?.clientHeight ?? 0, 'px', 'mm');

    const printArea = A4_HEIGHT_MM - (MARGIN_TOP_PAGE + MARGIN_BOTTOM_PAGE); // 287mm
    const usableHeight = printArea - footerHeight - headerHeight;
    const numberOfPages = Math.ceil(
      (heightPrintContent - footerHeight - headerHeight) / usableHeight
    );

    const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

    //? Clean old counter before if have before add new
    contentDocument.querySelectorAll('.count-page-wrapper').forEach((x) => x.remove());

    let currentPage = 1;
    while (currentPage <= numberOfPages) {
      const pageCount = iframeDocument.createElement('div');
      pageCount.classList.add('count-page-wrapper');
      const divHeight = 6; //5mm
      const baseBottomPosition = printArea - divHeight; //282mm
      const topPosition = baseBottomPosition + (currentPage - 1) * printArea;

      pageCount.innerHTML = `<div class="count-page" style="position: absolute; top: ${topPosition}mm; right: 0; height:${divHeight}mm">${currentPage}/${numberOfPages}</div>`;
      iframeDocument.body.appendChild(pageCount);

      currentPage++;
    }
  });

  contentWindow.addEventListener('afterprint', () => {
    contentDocument.querySelectorAll('.count-page-wrapper').forEach((x) => x.remove());
    if (typeof afterPrint === 'function') afterPrint();
  });

  contentWindow.focus(); //? IE fix
  onLoaded();
  contentWindow.print();
}

const removePage = `
function contains(selector, text) {
  var elements = document.querySelectorAll(selector);
  return Array.prototype.filter.call(elements, function(element){
    return RegExp(text).test(element.innerText);
  });
}
contains('p', /Trang.+[0-9]/).forEach(x => x.textContent = '')
`;

const setFooterSpace = `
const footer = document.querySelector('.page-footer');
const footerSpace = document.querySelector('.page-footer-space');
if (footer && footerSpace) {
  const tr = footerSpace.closest('tr');
  footerSpace.style.height = footer.clientHeight + 'px';
  tr.style.height = footer.clientHeight + 'px';
}
`;

const logoStyle = 'max-height: 100px; margin: 0 auto;';

/* const removeHeaderFooterPrint = `
  @media print {
    @page { margin: 0; }
    body { margin: 1.6cm; }
  }
`; */

/* const heightWithSingleHeader = numberOfPage * PAGE_HEIGHT;
  let requiredHeight = heightWithSingleHeader;
  if (numberOfPage > 1) {
    const headerHeight = header?.clientHeight ?? 0;
    const footerHeight = footer?.clientHeight ?? 0;
    requiredHeight -= (numberOfPage - 1) * (headerHeight + footerHeight);
  }
  printElement.style.height = `${requiredHeight}px`; */

/*   const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      try {
      } catch (error) {
        console.error('hide border failed:', error);
      }
    },
    onAfterPrint: () => {
      try {
      } catch (error) {
        console.log('hide border failed:', error);
      }
    },
    pageStyle,
  }); */

/*   const handlePrint = useCallback(() => {
    const iframe: HTMLIFrameElement = componentRef.current;

    if (!iframe) return;
    const contentWindow = iframe.contentWindow;
    contentWindow.focus(); //? IE fix
    contentWindow.print();
  }, []);
 */
